import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection
import EmailInput from './EmailInput';
import PasswordInput from './PasswordInput'; 
import GoogleLoginButton from './GoogleLoginButton';
import TermsAndConditions from './TermsAndConditions';
import PasswordResetModal from './PasswordResetModal'; // Import the PasswordResetModal component
import {jwtDecode} from 'jwt-decode'; // Import the jwt-decode library

const AuthOptions = ({ activeTab, handleGoogleLogin, onClose }) => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState(''); // State for username in business login
  const [password, setPassword] = useState(''); // State for password in both user and business login
  const [error, setError] = useState(''); // State to store any errors
  const [isPasswordStep, setIsPasswordStep] = useState(false); // Track if we are at password step for user login
  const [isResetModalOpen, setIsResetModalOpen] = useState(false); // State to manage the password reset modal

  const navigate = useNavigate(); // Initialize useNavigate for redirection

  // Handle email input changes
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  // Handle username and password input changes for business login
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  // Handle user email submission (move to password input step)
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setIsPasswordStep(true); // Move to the password step
  };

  // Handle forgot password for both user and business
  const handleForgotPassword = async () => {
    try {
      const identifier = activeTab === 'user' ? email : username; // Use email for user, username for business
      const resetUrl = `http://localhost:7254/auth/forgotPasword/${identifier}`;
      
      // Open forgot password URL in a new tab
      window.open(resetUrl, '_blank');
      
      // Open password reset modal in the main page
      setIsResetModalOpen(true);
    } catch (error) {
      console.error('Error during forgot password:', error);
      setError('An unexpected error occurred');
    }
  };

  // Handle user login submission (email + password)
  const handleUserLoginSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear any previous errors

    try {
      const response = await fetch(`${process.env.REACT_APP_LOGIN_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User Login Successful:', data);

        // Assuming the token is returned, save it to localStorage
        localStorage.setItem('user_token', data.token);

        // Redirect to the dashboard or relevant page
        navigate('/dashboard'); // Example: Navigate to the dashboard
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to login');
      }
    } catch (error) {
      console.error('Error during login:', error);
      setError('An unexpected error occurred');
    }
  };

  // Handle business login form submission
  const handleBusinessLoginSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear any previous errors

    try {
      const response = await fetch(`${process.env.REACT_APP_LOGIN_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }, 
        body: JSON.stringify({
          username: username,
          password: password,
        }),
      });
      console.log('Business login request data:', {
        username: username,
        password: password,
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Business Login Successful:', data);
        const accessToken = data.responseContent.access_token;

        // Assuming the token is returned, save it to localStorage
        localStorage.setItem('business_token', accessToken);

        // Redirect to /partneradmin after successful business login
        navigate('/partner/admin'); // Navigate to the PartnerAdmin page
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to login');
      }
    } catch (error) {
      console.error('Error during login:', error);
      setError('An unexpected error occurred');
    }
  };

  return (
    <div className="auth-options text-center flex flex-col h-full justify-top">
      {/* User Login Flow */}
      {!isPasswordStep && activeTab === 'user' ? (
        <>
          <h2 className="text-2xl font-bold mb-4 mt-6">
            User Sign In/ Sign up
          </h2>
          <EmailInput
            email={email}
            handleEmailChange={handleEmailChange}
            handleEmailSubmit={handleEmailSubmit}
          />
          <GoogleLoginButton handleGoogleLogin={handleGoogleLogin} />
        </>
      ) : isPasswordStep && activeTab === 'user' ? (
        <>
          <h2 className="text-2xl font-bold mb-4 mt-6">Enter Password</h2>
          <form onSubmit={handleUserLoginSubmit} className="mb-4">
            <PasswordInput
              password={password}
              handlePasswordChange={handlePasswordChange}
            />
            <div className="text-right text-blue-500 hover:underline cursor-pointer mb-4" onClick={handleForgotPassword}>
              Forgot password?
            </div>
            {error && (
              <p className="text-red-500 text-sm mb-4">{error}</p> // Display error message if login fails
            )}
            <button
              type="submit"
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition duration-200"
            >
              SignIn
            </button>
          </form>
        </>
      ) : !isPasswordStep && activeTab === 'business' ? (
        <>
          {/* Business Login Flow */}
          <h2 className="text-2xl font-bold mb-4 mt-6">
            Business Sign In
          </h2>
          <form onSubmit={handleBusinessLoginSubmit} className="mb-4">
            <div className="relative mb-4 mt-4">
              <input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={handleUsernameChange}
                className="border border-gray-300 rounded-lg py-2 pl-4 pr-4 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="relative mb-4 mt-4">
              <PasswordInput
                password={password}
                handlePasswordChange={handlePasswordChange}
              />
            </div>
            <div className="text-right text-blue-500 hover:underline cursor-pointer mb-4" onClick={handleForgotPassword}>
              Forgot password?
            </div>
            {error && (
              <p className="text-red-500 text-sm mb-4">{error}</p> // Display error message if login fails
            )}
            <button
              type="submit"
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition duration-200"
            >
              SignIn
            </button>
          </form>
          <p className="text-sm mt-1 font-bold">
            Want to partner with us? <a href="/partnerwithus" className="text-blue-500">Register with us</a>
          </p>
        </>
      ) : null}

      <TermsAndConditions />

      {/* Password Reset Modal */}
      {isResetModalOpen && (
        <PasswordResetModal
          onClose={() => setIsResetModalOpen(false)}
          identifier={activeTab === 'user' ? email : username}
        />
      )}
    </div>
  );
};

export default AuthOptions;
